import { GlobalSearchResultItemKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import { GlobalSearchItemPersistedModel } from "client/Features/Search/RecentSearch/Persistence/models";
import {
	ReactElement,
	useCallback,
	useEffect
} from "react";
import { AnnouncementItemView } from "./AnnouncementItemView";
import { AppItemView } from "./AppItemView";
import { CommunityItemView } from "./CommunityItemView";
import { CommunityPostItemView } from "./CommunityPostItemView";
import { ContactItemView } from "./ContactItemView";
import { EgnyteItemView } from "./EgnyteItemView";
import { EmailItemView } from "./EmailItemView";
import { EventItemView } from "./EventItemView";
import { IntegrationFlowItemView } from "./IntegrationFlowItemView";
import { KnowledgeItemView } from "./KnowledgeItemView";
import { DocumentItemView } from "./DocumentItemView";
import { ContactSource } from "client/Api/Email/contacts";
import { isValid } from "client/Features/Search/RecentSearch/Persistence/validation";
import { logNever } from "client_legacy/ndaw/errors";

export type ItemViewProps = {
	item: GlobalSearchItemPersistedModel;
	itemIndex: number;
	timezone: TimeZone;
	isMobile: boolean;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
	onRemove: (item: GlobalSearchItemPersistedModel) => void;
	onDismiss: () => void;
	tabIndex: number;
};

export const ItemView = (props: ItemViewProps): ReactElement | null => {
	const {
		item,
		itemIndex,
		timezone,
		isMobile,
		onOpenContactDetails,
		onRemove,
		onDismiss,
		tabIndex
	} = props;

	const removeHandler = useCallback(
		(): void => {
			onRemove(item);
		},
		[item, onRemove]
	);

	const isItemValid: boolean = isValid(item);

	useEffect(
		(): void => {
			if (!isItemValid) {
				console.error(`No valid data is set for the recently opened item with kind '${item.kind}'.`);
			}
		},
		[
			isItemValid,
			item.kind
		]
	);

	if (!isValid(item)) {
		return null;
	}

	switch (item.kind) {
		case GlobalSearchResultItemKind.Announcement:
			return (
				<AnnouncementItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.App:
			return (
				<AppItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.Community:
			return (
				<CommunityItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.CommunityPost:
			return (
				<CommunityPostItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.Contact:
			return (
				<ContactItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					onOpenContactDetails={onOpenContactDetails}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.Document:
			return (
				<DocumentItemView
					item={item}
					itemIndex={itemIndex}
					isMobile={isMobile}
					timezone={timezone}
					onDismiss={onDismiss}
					onRemove={removeHandler}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.EgnyteItem:
			return (
				<EgnyteItemView
					item={item}
					itemIndex={itemIndex}
					isMobile={isMobile}
					timezone={timezone}
					onDismiss={onDismiss}
					onRemove={removeHandler}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.Email:
			return (
				<EmailItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.Event:
			return (
				<EventItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.IntegrationFlow:
			return (
				<IntegrationFlowItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					tabIndex={tabIndex}
				/>
			);
		case GlobalSearchResultItemKind.KnowledgeItems:
			return (
				<KnowledgeItemView
					item={item}
					itemIndex={itemIndex}
					onRemove={removeHandler}
					timezone={timezone}
					itemScope={item.itemScope}
					tabIndex={tabIndex}
				/>
			);
		default:
			logNever(item);
			return null;
	}
};
ItemView.displayName = "ItemView";